import { useMemo, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Progress } from "@/components/ui/progress";
import { useToast } from "@/hooks/use-toast";
import { KeyRound, Shield, AlertTriangle } from "lucide-react";
import zxcvbn from "zxcvbn";

export function SetPasswordDialog() {
  const { toast } = useToast();
  const [open, setOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");

  const strength = useMemo(() => {
    if (!password) return null;
    return zxcvbn(password);
  }, [password]);

  const strengthScore = strength?.score ?? 0;
  const strengthLabel = useMemo(() => {
    if (!password) return "";
    if (strengthScore <= 1) return "Very Weak";
    if (strengthScore === 2) return "Weak";
    if (strengthScore === 3) return "Fair";
    return "Strong";
  }, [password, strengthScore]);

  const isStrongEnough = password.length >= 8 && strengthScore >= 3;
  const matches = password && confirmPassword && password === confirmPassword;

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!matches) {
      toast({
        title: "Passwords don't match",
        description: "Please make sure both passwords are the same.",
        variant: "destructive",
      });
      return;
    }

    if (!isStrongEnough) {
      toast({
        title: "Weak password",
        description: "Use at least 8 characters with a stronger mix of words, numbers, and symbols.",
        variant: "destructive",
      });
      return;
    }

    setIsLoading(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        throw new Error("You're not logged in. Please sign in and try again.");
      }

      const { error } = await supabase.auth.updateUser({ password });
      if (error) throw error;

      toast({
        title: "Password set",
        description: "You can now use this password to log in next time.",
      });

      setPassword("");
      setConfirmPassword("");
      setOpen(false);
    } catch (err: any) {
      toast({
        title: "Failed to set password",
        description: err?.message ?? "Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" className="gap-2">
          <KeyRound className="h-4 w-4" />
          Set a new password
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Set a new password</DialogTitle>
          <DialogDescription>
            If you were logged in via a reset link, set a password here so you can sign in later.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={onSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="pw">New password</Label>
            <Input
              id="pw"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              minLength={8}
              required
              autoComplete="new-password"
            />
            {password.length > 0 && (
              <div className="space-y-2">
                <div className="flex items-center justify-between text-xs">
                  <span className="text-muted-foreground">Password strength</span>
                  <span className={strengthScore < 3 ? "text-destructive" : "text-primary"}>
                    {strengthLabel}
                  </span>
                </div>
                <Progress value={strengthScore * 25} className="h-2" />
                {strength?.feedback?.warning && strengthScore < 3 && (
                  <div className="flex items-start gap-2 rounded-md border border-border bg-muted/50 p-2">
                    <AlertTriangle className="h-4 w-4 text-muted-foreground mt-0.5" />
                    <p className="text-xs text-muted-foreground">{strength.feedback.warning}</p>
                  </div>
                )}
                {strengthScore >= 3 && (
                  <div className="flex items-start gap-2 rounded-md border border-border bg-muted/50 p-2">
                    <Shield className="h-4 w-4 text-primary mt-0.5" />
                    <p className="text-xs text-muted-foreground">Looks good.</p>
                  </div>
                )}
              </div>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="pw2">Confirm new password</Label>
            <Input
              id="pw2"
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              minLength={8}
              required
              autoComplete="new-password"
            />
            {confirmPassword.length > 0 && password !== confirmPassword && (
              <p className="text-xs text-destructive">Passwords don't match</p>
            )}
          </div>

          <Button type="submit" className="w-full" disabled={isLoading || !matches || !isStrongEnough}>
            {isLoading ? "Saving..." : "Save password"}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}
